import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  FaCheckCircle,
  FaStar,
  FaArrowRight,
  FaTrophy,
  FaChartLine,
  FaBook,
  FaTicketAlt,
  FaTimes,
  FaTag
} from "react-icons/fa";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const NewStudentDashboard = () => {
  const [couponError, setCouponError] = useState(null);
  const [firstname, setFirstName] = useState("");
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Mock test access states
  const [mockTestPackageAccess, setMockTestPackageAccess] = useState(false);
  const [individualSubjectAccess, setIndividualSubjectAccess] = useState({});

  // Coupon modal states
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponValid, setCouponValid] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const subjects = [
    { key: 'Maths', name: 'Mathematical Reasoning Mock Test', displayName: 'Mathematical Reasoning Mock Test', price: 95 },
    { key: 'ThinkingSkills', name: 'Thinking Skills Mock Test', displayName: 'Thinking Skills Mock Test', price: 95 },
    { key: 'Writing', name: 'Writing Mock Test', displayName: 'Writing Mock Test', price: 95 },
    { key: 'Reading', name: 'Reading Mock Test', displayName: 'Reading Mock Test', price: 95 }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);
        setFirstName(user.firstName || user.username.split(" ")[0]);
        if (user.is_oc) {
          setUserType("oc");
        } else if (user.is_selective) {
          setUserType("selective");
        }

        try {
          // Check mock test package access
          const mockTestResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/mocktest-package-access/${user.id}`
          );
          const mockTestData = await mockTestResponse.json();
          setMockTestPackageAccess(mockTestData.hasAccess);

          // Check individual subject access
          const accessPromises = subjects.map(async (subject) => {
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/check-individual-access/${user.id}/${subject.key}`
            );
            const data = await response.json();
            return { [subject.key]: data.hasAccess };
          });

          const accessResults = await Promise.all(accessPromises);
          const accessMap = accessResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
          setIndividualSubjectAccess(accessMap);

        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  // Validate coupon function
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setCouponError(null); // Clear previous error
    setValidatingCoupon(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/validate-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode }),
      });

      const data = await response.json();

      if (data.valid) {
        setCouponValid(true);
        setCouponDiscount(data.coupon);
        setAppliedCoupon(couponCode);
        setCouponError(null);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setCouponError(data.error || "Invalid or expired coupon code");
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      setCouponValid(false);
      setCouponError("Error validating coupon. Please try again.");
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice) => {
    if (!couponValid || !couponDiscount) return originalPrice;

    if (couponDiscount.discount_type === 'percentage') {
      return originalPrice * (1 - couponDiscount.discount_value / 100);
    } else {
      // Fixed amount discount
      return Math.max(0, originalPrice - couponDiscount.discount_value);
    }
  };

  // Open coupon modal for package purchase
  const openPackageCouponModal = () => {
    if (mockTestPackageAccess) {
      navigate('/mock-tests');
    } else {
      setSelectedProduct({
        type: 'package',
        name: '10 Complete Mock Tests Package',
        price: 450,
        priceLabel: '$450'
      });
      setShowCouponModal(true);
    }
  };

  // Open coupon modal for individual subject purchase
  const openSubjectCouponModal = (subject) => {
    if (individualSubjectAccess[subject.key]) {
      navigate('/individual-mock-test', {
        state: {
          subject: subject.key,
          testType: 'individual',
          testName: subject.displayName,
          individualTest: true
        }
      });
    } else {
      setSelectedProduct({
        type: 'individual',
        subject: subject,
        name: subject.name,
        price: subject.price,
        priceLabel: `$${subject.price}`
      });
      setShowCouponModal(true);
    }
  };

  // Process payment with or without coupon
  // Process payment with or without coupon
  const processPayment = async () => {
    if (!studentId) {
      setCouponError("Please log in again");
      return;
    }

    setProcessingPayment(true);
    const stripe = await stripePromise;

    try {
      let endpoint;
      let body;

      if (selectedProduct.type === 'package') {
        if (appliedCoupon) {
          endpoint = "/api/payment/create-mocktest-package-session-with-coupon";
          body = {
            student_id: studentId,
            coupon_code: appliedCoupon
          };
        } else {
          endpoint = "/api/payment/create-mocktest-package-session";
          body = { student_id: studentId };
        }
      } else {
        // Individual subject
        if (appliedCoupon) {
          endpoint = "/api/payment/create-individual-mocktest-session-with-coupon";
          body = {
            student_id: studentId,
            subject: selectedProduct.subject.key,
            coupon_code: appliedCoupon
          };
        } else {
          endpoint = "/api/payment/create-individual-mocktest-session";
          body = {
            student_id: studentId,
            subject: selectedProduct.subject.key
          };
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        const result = await stripe.redirectToCheckout({ sessionId: data.id });
        if (result.error) {
          setCouponError(result.error.message);
          setProcessingPayment(false);
        } else {
          // Only reset if successful redirect
          resetCouponState();
        }
      } else {
        // Handle coupon validation error from backend
        if (data.error && (data.error.includes("coupon") || data.error.includes("expired"))) {
          setCouponError(data.error);
          // Clear the applied coupon since it's invalid
          setAppliedCoupon(null);
          setCouponValid(null);
          setCouponDiscount(null);
          setCouponCode("");
        } else {
          setCouponError(data.message || data.error || "Error creating payment session");
        }
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setCouponError("Payment failed. Please try again.");
      setProcessingPayment(false);
    }
  };

  // Reset coupon state
  const resetCouponState = () => {
    setCouponCode("");
    setCouponValid(null);
    setCouponDiscount(null);
    setAppliedCoupon(null);
    setSelectedProduct(null);
    setCouponError(null);
  };

  // Close modal and reset
  const closeModal = () => {
    setShowCouponModal(false);
    resetCouponState();
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponValid(null);
    setCouponDiscount(null);
    setCouponCode("");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {processingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
            <p className="mt-4 text-gray-700">Redirecting to payment...</p>
          </div>
        </div>
      )}

      {/* Coupon Modal */}
      {showCouponModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b">
              <div className="flex items-center space-x-2">
                <FaTag className="text-purple-500 text-xl" />
                <h2 className="text-xl font-bold text-gray-800">Apply Coupon Code</h2>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Product Details */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">{selectedProduct.name}</h3>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Original Price:</span>
                  <span className="text-lg font-bold text-gray-800">{selectedProduct.priceLabel}</span>
                </div>
                {appliedCoupon && couponDiscount && (
                  <>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t">
                      <span className="text-green-600">Discount Applied:</span>
                      <span className="text-green-600 font-semibold">
                        {couponDiscount.discount_type === 'percentage'
                          ? `${couponDiscount.discount_value}% OFF`
                          : `$${couponDiscount.discount_value} OFF`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-600">Final Price:</span>
                      <span className="text-2xl font-bold text-green-600">
                        ${calculateDiscountedPrice(selectedProduct.price).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Coupon Input */}
              {!appliedCoupon ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Coupon Code
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError(null);
                        }}
                        placeholder="e.g., SAVE20"
                        className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${couponError ? 'border-red-500' : 'border-gray-300'
                          }`}
                        autoFocus
                      />
                      <button
                        onClick={validateCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {validatingCoupon ? "..." : "Apply"}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" size={14} />
                        <p className="text-green-600 font-semibold">Coupon Applied Successfully!</p>
                      </div>
                      <p className="text-green-500 text-sm mt-1">Code: {appliedCoupon}</p>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              {/* Error message - Show this ABOVE coupon input when there's an error */}
              {couponError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <FaTimes className="text-red-500 mr-2 flex-shrink-0" size={14} />
                    <p className="text-red-600 text-sm">{couponError}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={processPayment}
                  disabled={processingPayment}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg hover:from-green-600 hover:to-teal-600 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingPayment ? "Processing..." : "Proceed to Payment"}
                  {appliedCoupon && !processingPayment && ` ($${calculateDiscountedPrice(selectedProduct.price).toFixed(2)})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">
            Welcome back, <span className="text-teal-600">{firstname || "Student"}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-600">Continue your learning journey</p>
        </div>

        <div className="space-y-6 sm:space-y-8">
          {/* Mock Test Package Card with Yellow/Orange Gradient Border */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                  <div className="bg-yellow-100 p-3 sm:p-4 rounded-full group-hover:bg-yellow-50 transition flex-shrink-0 self-center sm:self-start">
                    <FaStar className="text-yellow-500 text-2xl sm:text-3xl group-hover:text-yellow-600 transition" />
                  </div>
                  <div className="flex-1">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                        Mock Test Center
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600 mt-1 group-hover:text-gray-700 transition">
                        Unlock your potential with our comprehensive mock test packages or choose individual subject mock tests tailored to your needs.
                        Our tests are designed to simulate real exam conditions providing you with perfect opportunity for targeted practice and enhanced performance.
                        Start your journey to success today.
                      </p>
                    </div>

                    <ul className="mt-4 sm:mt-6 space-y-1 sm:space-y-2">
                      {/* 10 Complete Mock Tests Package */}
                      <li className="flex items-start p-2 rounded-lg">
                        <FaCheckCircle className={`mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base ${mockTestPackageAccess ? 'text-green-500' : 'text-yellow-500'
                          }`} />
                        <div className="flex-1">
                          <span className="text-sm sm:text-base font-medium">
                            10 Complete Mock Tests Package ($450)
                          </span>
                          {mockTestPackageAccess ? (
                            <button
                              onClick={() => navigate('/mock-tests')}
                              className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full hover:bg-green-200 transition"
                            >
                              Purchased - Click to start
                            </button>
                          ) : (
                            <button
                              onClick={openPackageCouponModal}
                              className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full hover:bg-yellow-200 transition"
                            >
                              Click to buy
                            </button>
                          )}
                        </div>
                      </li>

                      {/* Individual Subject Mock Tests */}
                      {subjects.map((subject) => (
                        <li key={subject.key} className="flex items-start p-2 rounded-lg">
                          <FaCheckCircle className={`mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base ${individualSubjectAccess[subject.key] ? 'text-green-500' : 'text-yellow-500'
                            }`} />
                          <div className="flex-1">
                            <span className="text-sm sm:text-base font-medium">
                              {subject.name} (${subject.price})
                            </span>
                            {individualSubjectAccess[subject.key] ? (
                              <button
                                onClick={() => navigate('/individual-mock-test', {
                                  state: {
                                    subject: subject.key,
                                    testType: 'individual',
                                    testName: subject.displayName,
                                    individualTest: true
                                  }
                                })}
                                className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full hover:bg-green-200 transition"
                              >
                                Purchased - Click to start
                              </button>
                            ) : (
                              <button
                                onClick={() => openSubjectCouponModal(subject)}
                                className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full hover:bg-yellow-200 transition"
                              >
                                Click to buy
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practice-a-thon Card with Blue/Purple Gradient Border */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 hover:from-blue-300 hover:to-purple-400 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                  <div className="bg-blue-100 p-3 sm:p-4 rounded-full group-hover:bg-blue-50 transition flex-shrink-0 self-center sm:self-start">
                    <FaBook className="text-blue-500 text-2xl sm:text-3xl group-hover:text-blue-600 transition" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                          Practice-a-thon
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 mt-1 group-hover:text-gray-700 transition">
                          Unlock individual subjects and features
                        </p>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium group-hover:bg-blue-50 group-hover:text-blue-700 transition mt-2 sm:mt-0">
                        $109/per subject
                      </span>
                    </div>

                    <div className="mt-4 sm:mt-6">
                      <ul className="space-y-1 sm:space-y-2">
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaCheckCircle className="text-green-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Practice Tests</span>
                        </li>
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaTrophy className="text-yellow-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Badge Test</span>
                        </li>
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaChartLine className="text-blue-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Detailed Analytics</span>
                        </li>
                      </ul>
                    </div>

                    <div className="mt-6 sm:mt-8 flex justify-end">
                      <button
                        onClick={() => navigate('/subject-tests')}
                        className="text-blue-600 hover:text-blue-800 font-medium flex items-center group text-sm sm:text-base"
                      >
                        <span className="group-hover:underline">Explore Features</span>
                        <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStudentDashboard;